import {Title, Meta} from '@angular/platform-browser';
import {Component, EventEmitter, Inject, OnDestroy, OnInit, Output} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {Store} from '@ngrx/store';
import {TranslateService} from '@ox/translate';
import {AdobeLaunchService, FeatureFlagService, selectActiveVehicle, selectAuthState, selectFeatureFlag, TrackingService} from '@ox/data';
import {FeatureCopy, GenericResponse, Outs, PlanBillingType, Plans, PriceItem, ProductCopy, UIFeatureFlag, UIFeatureFlagsEnum, Vehicle} from '@ox/typings';
import {AutoDestroy} from '@ox/utils';
import {SessionStorageService} from '@ox/utils';
import {Subject, combineLatest, forkJoin, map, take, takeUntil, tap, switchMap, of, takeWhile, catchError, throwError} from 'rxjs';
import {CheckoutService} from '../../services/checkout/checkout.service';
import {PageNameService} from '../../services/page-name-resolver/page-name.service';
import {PdpService} from '../../services/pdp/pdp.service';
import {APP_BASE_HREF} from '@angular/common';
import {find, fill, split, sortBy} from 'lodash';
import {AEMFeatureList, AEMPriceItem, ShopDataService} from '@webu/data';
import {OX_KEYS} from '@ox/config';
import {LocationService, CheckoutNavigationService} from '@webu/ui';
import {getFeaturesGroupByFeatureCatergoryCode, LaunchDarklyUtils} from '@ox/utils';
import {ShopVariationService} from '@webu/ui';

@Component({
  selector: 'pdp-page',
  templateUrl: './plan-detail-page.component.html',
  styleUrls: ['./plan-detail-page.component.scss']
})
export class PlanDetailPageComponent implements OnInit, OnDestroy {
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();
  content: any = {};
  public loading = true;
  public isError = false;
  public pricingData: any;
  public aemOnstarURL: any;
  @Output() public signInEmitter = new EventEmitter();
  bml = window?.gm?.bml;

  //hard-coding as a fallback url, will revisit cms issue later
  tempOnstarAEMURL = 'https://contentdelivery.ext.gm.com/content/dam';

  product = '';
  planId = '';
  activeVehicle?: Vehicle;
  activeVehicle$ = this.store.select(selectActiveVehicle);
  userState$ = this.store.select(selectAuthState);
  authenticationStatus: string;
  protected readonly baseHref = this._baseHref.split('/').slice(0, -1)?.join('/');
  errorMessageDetail: any;
  vehicleAssociatedStatus = false;
  familyMemberStatus: boolean;
  inEligibleUser: boolean;
  isPlanPurchasedStatus: boolean;
  copyDocKey: string;
  isVehicleOnstarActive: boolean;
  pricineAPICopyDocKey: string;
  sharedNavEnabled = this.translate.get('isSharedNavEnabled');
  pdpVariation: string;
  compareData: {comparePlanApiResponse:Plans[],copyApiResponse:ProductCopy[]};
  featureList: Array<any>;
  featureListLength = 0;
  planCode: string;
  isCVOVehicle: boolean;
  isCheckoutRedesignEnabled: boolean;
  isBuyNowLoading = false;
  isInterimCatalogEnabled: boolean;
  showComparePlansButton = true;
  private unAuthCatalogBuyNowFlow = false;
  oldPlan: string;
  newPlan: string;
 
  checkoutRedesign$ = this.store
    .select(selectFeatureFlag({id: UIFeatureFlag.CHECKOUT, group: 'UI'}))
    .pipe(
      takeWhile((val) => !val, true),
      map((flag) => this.isCheckoutRedesignEnabled = flag === 'true')
    ).subscribe();

  interimCatalog$ = this.featureFlagService.featureFlags$
    .pipe(
      map(ff => ff.UI[UIFeatureFlagsEnum.OC_INTERIM_CATALOG]),
      tap(interimCatalogFF => this.isInterimCatalogEnabled = interimCatalogFF)
    ).subscribe();


  constructor(
    private shopDataService: ShopDataService,
    private titleService: Title,
    private store: Store,
    private activatedRoute: ActivatedRoute,
    private translate: TranslateService,
    private checkoutService: CheckoutService,
    public trackingService: TrackingService,
    public pageNameService: PageNameService,
    private router: Router,
    private sessionStorage: SessionStorageService,
    private pdpService: PdpService,
    @Inject(APP_BASE_HREF) private _baseHref: string,
    private metaService: Meta,
    private location: LocationService,
    private shopVariationService: ShopVariationService,
    private checkoutNavigationService: CheckoutNavigationService,
    private dataLayer: AdobeLaunchService,
    private featureFlagService: FeatureFlagService
  ) {}

  ngOnDestroy(): void {
    this.isBuyNowLoading = false;
  }

  async ngOnInit(): Promise<void> {
    this.isBuyNowLoading = false;
    this.product = this.activatedRoute.snapshot.paramMap.get('product') ?? '';
    this.pdpVariation = await this.shopVariationService.callVariation(this.product);
    this.activeVehicle$.subscribe((vehicle) => {
      if (vehicle && vehicle.vin !== this.activeVehicle?.vin) {
        this.activeVehicle = vehicle;
        this.pricingData = undefined;
        this.content = undefined;
        if (this.activeVehicle?.onStarActive !== true) {
          window.location.href = '../onstar/activate';
        }
        this.getPricingData(this.product, true);
      }
    });

    this.userState$.pipe(
      take(1),
      tap((authState) => {
        if (authState === 'unauthenticated') {
          this.isInterimCatalogEnabled ? this.unauthInterimCatalogFlow() : this.getPricingData(this.product, false);
        }
        if (authState === 'authenticated' && !this.activeVehicle) {
          this.vehicleAssociatedStatus = true;
          this.copyDocKey = OX_KEYS.PDP.ERROR.isAssociatedKey;
          this.getPricingData(this.product, false);
        }
        this.authenticationStatus = authState;
      }))
      .subscribe();

      this.getPlanData();
  }

  private unauthInterimCatalogFlow() {
    this.isError = false;
    this.loading = true;
    this.showComparePlansButton = false;
    this.unAuthCatalogBuyNowFlow = true;

    this.pdpService
      .getJsonFile(this.product, this.pdpVariation, this.interimCatalogYear())
      .pipe(
        take(1),
        tap((content) => {
          this.content = content;
          this.aemMetaData();

          const pricing = content.product.templates.components.find(i => i.type === 'PRICING') as unknown as AEMPriceItem; // PRICING AEM OBJECT
          if(!pricing) {
            throw new Error('Pricing Data Missing in AEM JSON');
          }

          const aemPriceData = {
            data: pricing
          };

          this.pricingData = aemPriceData;
          this.renderPDP();
      }),
      catchError(err => throwError(() => this.onError(err)))
    ).subscribe();
  }

  public getPricingData(product: string, isAuthenticated: boolean) {
    this.isError = false;
    this.loading = true;
    this.pdpService.getPricingData(isAuthenticated, product, this.activeVehicle?.vin).subscribe({
      next: (response) => {
        if(!response.data && response.messages) {
          this.isCVOVehicle = this.cvoCheck(response.messages);
          this.loading = false;
          throw new Error('No Offers - CVO Vehicle');
        }
        this.pricingData = response;
        this.renderPDP();

      },
      error: (error) => this.onError(error)
    });
  }

  private renderPDP() {
    this.dataLayer.setPdpProducts(this.pricingData.data);
    this.planId = this.pricingData.data?.planId;
    this.titleService.setTitle(this.pricingData?.data?.title);

    if (
      (this.pricingData?.data?.code && this.pricingData?.data?.subCode)
      || !this.pricingData?.data
      || this.pricingData?.error
      || this.pricingData?.data?.error
      || this.pricingData.data == null
      || (this.pricingData.serverErrorMsgs && this.pricingData.serverErrorMsgs.length > 0)
    ) {
      this.onError('Error getting pricing data');
    }

    this.activatedRoute.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
      if(params['oldPlan']&& params['newPlan']){
        this.handleNameCheck(params['oldPlan'], params['newPlan']);
      }
      if (params['redirect'] === 'checkout') {
        if(params['interim'] === 'true') this.unAuthCatalogBuyNowFlow = true;
        const offerId = params['offerId'];
        this.buyNow(offerId);
      }
    });

    forkJoin([
      this.errorMessage(),
      (this.content && this.content?.product) ? of({}) : this.getAEMContent(),
      this.shopDataService.getComparePlanData(
        this.authenticationStatus ==='authenticated',
        this.activeVehicle?.vin ? {vin:this.activeVehicle?.vin} : {}
      ),
      this.shopDataService.getCopyData()
    ]).pipe(
      switchMap((response:any)=>{
        return of({
          /* Taking responses from copy and compare apis and sending there */
          comparePlanApiResponse: response[2]?.plans,
          copyApiResponse: response[3]?.products
        });
      }
    )).subscribe({
      next: res => this.compareData = res,
      error: (error: any) => this.onError(error),
      complete: () => {
        this.handleFeatureGroup();
        this.loading = false;
      }
    });
  }

  handleNameCheck(oldPlan: string, newPlan:string){
    this.oldPlan = oldPlan;
    this.newPlan= newPlan
    .replace(/[-–—]/g, ' ')
    .trim()
    .replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());   
  }


  private interimCatalogYear() {
    if(!this.isInterimCatalogEnabled) return '';
    else if(/data/i.test(this.product)) return '';
    return (this.activeVehicle && this.activeVehicle.year) ? this.getYear(this.activeVehicle.year) : this.getYear(this.activatedRoute.snapshot.queryParamMap.get('year') ?? '');
  }

  private getYear(_year: string) {
    if(_year == '' || _year == null) return '2025';
    else if(/2025|2024/i.test(_year)) return _year;
    const year: number = +_year;
    return year <= 2024 ? '2024' : '2025';
  }

  cvoCheck(messages: string[]) {
    return messages.find(x => x.includes('accountType'))?.split('=')[1].toUpperCase() === 'CV';
  }

  errorMessage() {
    return combineLatest([
      this.store.select(selectActiveVehicle),
      this.shopDataService.getCopyData()
    ])
      .pipe(
        take(1),
        map((response) => {
          let errormessageInfo: any;
          if (Object.prototype.hasOwnProperty.call(this.pricingData?.data, 'copyDocKey')) {
            this.pricineAPICopyDocKey = this.pricingData?.data?.copyDocKey;
            errormessageInfo = this.getErrorMessageInfo(response[1], this.pricineAPICopyDocKey);
            this.inEligibleUser = errormessageInfo?.copyDocKey === OX_KEYS.PDP.ERROR.inEligibleKey ? true : false;
            this.isPlanPurchasedStatus = errormessageInfo?.copyDocKey === OX_KEYS.PDP.ERROR.planPurchasedKey ? true : false;
          }

          this.familyMemberStatus = response[0]?.roleCode === 'MEMBER' ? true : false;
          if (this.familyMemberStatus) {
            errormessageInfo = this.getErrorMessageInfo(response[1], OX_KEYS.PDP.ERROR.familyMemberKey);
          }

          this.isVehicleOnstarActive = response[0]?.statusCode === 'VSINACTIVE' ? false : true;
          if (!this.isVehicleOnstarActive) {
            errormessageInfo = this.getErrorMessageInfo(response[1], OX_KEYS.PDP.ERROR.inActiveVinKey);
          }

          this.errorMessageDetail = [{
            isActiveVin: this.isVehicleOnstarActive,
            isFamilyMember: this.familyMemberStatus,
            isPlanPurchased: this.isPlanPurchasedStatus,
            isEligible: !this.inEligibleUser,
            errorMessageText: errormessageInfo?.copyDocValue,
            isBannerError:
              !this.isVehicleOnstarActive ||
              this.familyMemberStatus ||
              this.inEligibleUser ||
              this.isPlanPurchasedStatus
          }];
        }));
  }


  getErrorMessageInfo(shopCopy: any, pricingCopyDocKey: any) {
    return find(shopCopy.generic, (copy: any) => {
      return copy.copyDocKey === pricingCopyDocKey;
    });
  }

  getAEMContent() {
    return this.pdpService
      .getJsonFile(this.product, this.pdpVariation, this.interimCatalogYear())
      .pipe(
        take(1),
        map((content) => {
          this.content = content;
          this.aemMetaData();
        }));
  }

  private aemMetaData() {
    const id = 'onstar_dam_prefix';
    this.metaService.addTag({
      name: 'robots',
      content: this.content?.product?.templates?.allowIndex ? 'index' : 'noindex'
    });
    this.translate
      .get(id)
      .pipe(takeWhile(res => res !== id))
      .subscribe({
        next: (cmsValue) => this.aemOnstarURL = cmsValue,
        error: (error) => this.onError(error)
      });

    if (
      typeof this.aemOnstarURL === 'undefined' ||
      !this.aemOnstarURL.includes('http')
    ) {
      this.aemOnstarURL = this.tempOnstarAEMURL;
    }
  }

  onError(err: string) {
    console.error(err);
    this.isError = true;
    this.loading = false;
    this.isBuyNowLoading = false;
  }

  getPrimaryButtonAction(offerId: string): string {
    const {primaryButtonAction} = this.pricingData?.data?.price.find((i: PriceItem) => i.offerId === offerId) as PriceItem || {};
    if(this.unAuthCatalogBuyNowFlow && !primaryButtonAction) {
      return 'BAD_OFFER_ID_UNAUTH_INTERIM';
    }
    return primaryButtonAction || this.pricingData?.data?.primaryButtonAction;
  }

  redirectTo(url: string) {
    this.location.handleHref(url);
  }

  handleReview(offerId: string) {
    if (this.isCheckoutRedesignEnabled) {
      this.navigateToCheckoutRedesign(offerId);
    } else {
      if(this.pdpVariation) LaunchDarklyUtils.broadcast$(this.productNameConversion(this.product));
      this.sessionStorage.setAsJson('ReviewId', offerId);
      this.sessionStorage.setAsJson('ManagePlanInfo', {pageType: 'REVIEW', offerId: offerId});
      this.sessionStorage.setAsJson(OX_KEYS.ONSTAR_CHECKOUT, true);
      this.redirectTo(`${this.baseHref}/onstar/review`);
    }
  }

  handleCheckout(offerId: string) {
    if (this.isCheckoutRedesignEnabled) {
      this.navigateToCheckoutRedesign(offerId);
    } else {
      this.sessionStorage.setAsJson('ThemeId', offerId);
      this.sessionStorage.setAsJson('ReviewId', offerId);
      this.sessionStorage.setAsJson(OX_KEYS.ONSTAR_CHECKOUT, false);
      if(this.pdpVariation) LaunchDarklyUtils.broadcast$(this.productNameConversion(this.product));
      this.redirectTo(`${this.baseHref}/shop/checkout`);
    }
  }

  handleDefault(offerId: string) {
    if (this.isCheckoutRedesignEnabled) {
      this.navigateToCheckoutRedesign(offerId);
    } else {
      this.checkoutService.checkout(offerId);
    }
  }

  navigateToCheckoutRedesign(offerId: string) {
    if(this.pdpVariation) LaunchDarklyUtils.broadcast$(this.productNameConversion(this.product));
  
    const priceInfo = this.pricingData?.data?.price?.find((price: any) => (price.offerId === offerId) || (price.planCode === offerId));
    if (!priceInfo) return console.error('Price info not found');

    let billingType: PlanBillingType;
    if (priceInfo?.billingFrequency === 'MONTHLY') billingType = 'RECURRING';
    else if (priceInfo?.billingFrequency === 'Yearly') billingType = 'Yearly';
    else billingType = priceInfo?.billingFrequency ?? null;

    const plancode = /data/i.test(this.product) ? priceInfo?.planCode : this.pricingData?.data?.planCode;

    this.checkoutNavigationService.goToCheckout([plancode], this.router.url, '', billingType)
      .pipe(take(1))
      .subscribe({
        next: () => this.isBuyNowLoading = false,
        error: (err) => this.onError(err)
      });
  }

  productNameConversion(productName: string) {
    const productString = `${productName.toLocaleUpperCase()}.BUY.NOW.CONVERSION`;
    return productString;
  }

  buyNow(event: any) {
    this.isBuyNowLoading = true;
    const offerId = typeof event?.detail === 'object' ? event?.detail?.offerDetails?.offerId : event.detail || event;
    const planName = event?.detail?.offerDetails?.planName;
    const primaryButtonAction = 
    (event?.detail?.offerDetails?.page === 'comparePlanModal'
      && this.authenticationStatus === 'authenticated'
      && this.isCheckoutRedesignEnabled)? 'COMPARE_PLAN_BUY_NOW' : this.getPrimaryButtonAction(offerId);
    
    // handle primary button CTAs
    switch (true) {
      case /REVIEW/.test(primaryButtonAction):
        this.handleReview(offerId);
        break;
      case /OC_CHECKOUT/.test(primaryButtonAction):
        this.handleCheckout(offerId);
        break;
      case /MY[ _]PLANS/.test(primaryButtonAction):
        this.router.navigate(['./onstar/myplans']);
        break;
      case /Sign In|SIGN_IN/.test(primaryButtonAction) && this.authenticationStatus === 'unauthenticated':
        this.signIn(planName, offerId);
        break;
      case /COMPARE_PLAN_BUY_NOW/.test(primaryButtonAction):
        this.comparePlanBuyNow(planName, offerId);
        break;
      case /BAD_OFFER_ID_UNAUTH_INTERIM/.test(primaryButtonAction):
        this.resetUnAuthCatalogData();
        break;
      default:
        this.handleDefault(offerId);
    }
  }

  // Remove URL query params from unauth 'buy now' flow as the offerId from AEM does not match BFF offerId
  private resetUnAuthCatalogData() {
    this.isBuyNowLoading = false;
    this.unAuthCatalogBuyNowFlow = false;
    this.router.navigate([], {replaceUrl: true});
  }

  signIn(planName?: string, offerId?: string) {
    const gbpe = window?.gbpe;
    const planUrl = planName ? fill(split(this.router.url, '/'), planName, 3, 4).join('/') : this.router.url;
    const redirectUrl = new URL(this.baseHref + planUrl, window.location.origin);
    
    if(offerId) {
      redirectUrl.searchParams.set('redirect','checkout');
      redirectUrl.searchParams.set('offerId', offerId);
      if(this.unAuthCatalogBuyNowFlow) redirectUrl.searchParams.set('interim', 'true');
    }

    if(this.pdpVariation) LaunchDarklyUtils.broadcast$(this.productNameConversion(this.product));
    gbpe.auth.loginRedirect(redirectUrl.toString());
  }

  refresh() {
    const currentUrl = this.router.url;
    this.router.navigateByUrl('/', {skipLocationChange: true}).then(() => {
      this.router.navigate([currentUrl]);
    });
  }
  handleFeatureGroup(): void {
    if(this.isInterimCatalogEnabled) {
      const feature_list = this.content.product.templates.components.find((i: {type: string;}) => i.type === 'FEATURE_TABLE') as unknown as AEMFeatureList; // FEATURE LIST AEM OBJECT FOR COMPARE PLANS
      if(feature_list && feature_list.plans) {
        this.featureList = feature_list.plans ?? [];
        this.featureList = sortBy(this.featureList, 'key');
        this.featureListLength = this.featureList.length;
      }
    } else {
      const product = this.shopDataService.getProductCopyByCode(this.pricingData?.data?.planCode);
      const allProducts = this.shopDataService.getAllCopyProducts();
      /* We want to to make sure that we get a product and copy is giving data */
      if (product && allProducts) {
        const featuresFromComapre = this.compareData.comparePlanApiResponse.find((plan: Plans) => plan.code === product.code)?.features; 
        const codeList = (featuresFromComapre as unknown as [FeatureCopy])?.map(({code}) => code);
        const sortedCodes = codeList?.sort();
        const featureList = getFeaturesGroupByFeatureCatergoryCode(
          product,
          allProducts,
          sortedCodes?.map(i => ({code:i})) as unknown as FeatureCopy[] ?? undefined
        ) as GenericResponse<Outs>;
  
        this.featureList = [];
        Object.keys(featureList).forEach(k => {
          this.featureList.push({key: k, list: sortBy(featureList[k], 'code') as any});
        });
        this.featureList = sortBy(this.featureList, 'key');
        /* Get the length of the feature list*/
        this.featureListLength = Object.keys(featureList).length;
      }
    }
  }

  getPlanData(){
    this.shopDataService.getCopyData().pipe(takeUntil(this.destroy$)).subscribe((res: any)=> {
      const planDetails = res.products?.find((product:any) => product.seoUrl?.toLowerCase() === this.product.toLowerCase());
      this.planCode = planDetails?.code;
    });
  }
  
  /**
   * Clicking Buy Now from Compare Plan Modal will redirect to respective PDP with checkout redirect param
   * @param planName 
   * @param offerId 
   */
  comparePlanBuyNow(planName: string, offerId:string) {
    const planUrl = planName ? fill(split(this.router.url, '/'), planName, 3, 4).join('/') : this.router.url;
    window.location.href = `${this.baseHref}${planUrl}?redirect=checkout&offerId=${offerId}`;
  }
}
